import { useCallback, useEffect, useState } from 'react'
import {
  PUSH_CHANGED_EVENT, currentSubscription, disablePush, enablePush, isIos, permission, pushApi, pushSupport,
  type PushState,
} from '../lib/push'
import { Button, Callout, Card, CardContent, CardHeader, CircularProgress, IconButton, useConfirm } from './ui'

function when(iso: string | null): string {
  if (!iso) return 'mai'
  return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/**
 * "Notifiche sul telefono" nelle preferenze: stato di questo dispositivo con Attiva/Disattiva, prova di invio, elenco
 * dei propri dispositivi (si possono togliere). Con il flag spento il server risponde `enabled: false` e il pannello
 * non compare.
 */
export function PushDevicePanel() {
  const confirm = useConfirm()
  const [state, setState] = useState<PushState | null>(null)
  const [thisEndpoint, setThisEndpoint] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [info, setInfo] = useState<{ tone: 'success' | 'error' | 'warning'; text: string } | null>(null)
  const support = pushSupport()

  const load = useCallback(async () => {
    const s = await pushApi.get().catch(() => null)
    setState(s)
    const sub = await currentSubscription().catch(() => null)
    setThisEndpoint(sub?.endpoint ?? null)
  }, [])

  useEffect(() => {
    void load()
    const onChange = () => { void load() }
    window.addEventListener(PUSH_CHANGED_EVENT, onChange)
    return () => window.removeEventListener(PUSH_CHANGED_EVENT, onChange)
  }, [load])

  if (!state?.enabled) return null

  const thisActive = thisEndpoint !== null && state.devices.some(d => d.endpoint === thisEndpoint)
  const denied = permission() === 'denied'

  async function activate() {
    if (!state?.publicKey) return
    setBusy(true)
    setInfo(null)
    try {
      const r = await enablePush(state.publicKey)
      setInfo(r === 'ok'
        ? { tone: 'success', text: 'Notifiche attive su questo dispositivo. Tocca «Invia una prova» per verificarle.' }
        : { tone: 'warning', text: 'Permesso negato. Consenti le notifiche per Equal CRM nelle impostazioni del telefono o del browser, poi riprova.' })
    } catch {
      setInfo({ tone: 'error', text: 'Attivazione non riuscita su questo dispositivo. Riprova tra poco.' })
    } finally {
      setBusy(false)
    }
  }

  async function deactivate() {
    setBusy(true)
    setInfo(null)
    try { await disablePush() } finally { setBusy(false) }
  }

  async function test() {
    setBusy(true)
    setInfo(null)
    try {
      await pushApi.test()
      setInfo({ tone: 'success', text: 'Prova inviata: arriva su tutti i tuoi dispositivi attivi entro pochi secondi.' })
    } catch (e) {
      setInfo({ tone: 'error', text: e instanceof Error ? e.message : 'Prova non inviata.' })
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: number, name: string) {
    if (!await confirm({ title: 'Togliere il dispositivo?', message: `${name} non riceverà più le notifiche.`, confirmLabel: 'Togli', destructive: true })) return
    await pushApi.removeById(id).catch(() => undefined)
    await load()
  }

  return (
    <Card variant="outlined" padding="none" className="mb-3">
      <CardHeader title="Notifiche sul telefono" icon="mobile_friendly" />
      <CardContent>
        <p className="text-muted md-typescale-body-small">
          Quello che arriva subito nel centro notifiche arriva anche sul telefono, con l&apos;azione da fare: toccando la
          notifica si apre la pratica. Funziona su Android e su iPhone; sotto scegli per quali eventi.
        </p>

        {info && <Callout tone={info.tone} role="status" className="mb-3">{info.text}</Callout>}

        {support === 'ios-needs-install' && (
          <Callout tone="info" icon="ios_share" className="mb-3">
            Su iPhone le notifiche arrivano solo dall&apos;app: in Safari tocca <strong>Condividi</strong> e poi
            <strong> Aggiungi alla schermata Home</strong>, apri Equal CRM dall&apos;icona e torna qui per attivarle
            (serve iOS 16.4 o successivo).
          </Callout>
        )}
        {support === 'unsupported' && (
          <Callout tone="warning" className="mb-3">
            {isIos()
              ? 'Questo iPhone non supporta le notifiche del CRM: aggiorna iOS alla versione 16.4 o successiva.'
              : 'Questo browser non supporta le notifiche push. Usa Chrome, Edge, Firefox o Safari aggiornati.'}
          </Callout>
        )}
        {support === 'supported' && denied && (
          <Callout tone="warning" className="mb-3">
            Le notifiche per Equal CRM sono bloccate su questo dispositivo: sbloccale nelle impostazioni del telefono
            (o del browser, dall&apos;icona accanto all&apos;indirizzo) e poi tocca «Attiva su questo dispositivo».
          </Callout>
        )}

        {support === 'supported' && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {thisActive
              ? <Button variant="outlined" icon="notifications_off" disabled={busy} onClick={() => void deactivate()}>Disattiva su questo dispositivo</Button>
              : <Button variant="filled" icon="notifications_active" disabled={busy} onClick={() => void activate()}>Attiva su questo dispositivo</Button>}
            <Button variant="text" icon="send" disabled={busy || state.devices.length === 0} onClick={() => void test()}>Invia una prova</Button>
            {busy && <CircularProgress size="sm" />}
          </div>
        )}

        {state.devices.length > 0 ? (
          <ul className="list-unstyled mb-0">
            {state.devices.map(d => (
              <li key={d.id} className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom">
                <div>
                  <div className="md-typescale-emphasized-body-medium">
                    {d.device}{d.endpoint === thisEndpoint ? ' (questo dispositivo)' : ''}
                  </div>
                  <div className="text-muted md-typescale-body-small">
                    Attivato il {when(d.createdAt)} · ultima consegna {when(d.lastSuccessAt)}
                    {!d.active && ' · non raggiungibile'}
                  </div>
                </div>
                <IconButton icon="delete" aria-label={`Togli ${d.device}`} onClick={() => void remove(d.id, d.device)} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-0 md-typescale-body-medium">Nessun dispositivo attivo.</p>
        )}
      </CardContent>
    </Card>
  )
}
